import React, { useEffect, useState } from "react";
import { Form, Input, Button, Radio, Select, Checkbox, Steps } from "antd";
import { useNavigate } from "react-router-dom";
import { useAuth, type UserProps } from "../../hooks/auth";
import { BRAZIL_STATES } from "../../constants/brazilStates";
import { COUNTRIES } from "../../constants/countries";
import MobileLayout from "../../components/MobileLayout";
import {
  MobileFormCard,
  Title,
  Subtitle,
  StepContainer,
  StepActions,
  RadioGroup,
  StepsWrap,
} from "./styles";

const LIBRAS_LEVEL_DB = [
  { value: "basico", label: "Básico" },
  { value: "intermediario", label: "Intermediário" },
  { value: "avancado", label: "Avançado / Fluente" },
] as const;

type CompleteRegistrationProps = {
  mode?: "complete" | "edit";
};

const CompleteRegistration: React.FC<CompleteRegistrationProps> = ({
  mode = "complete",
}) => {
  const isEditMode = mode === "edit";
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { user, updateUserProfile, loadingAuth } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    if (isEditMode || !user) return;
    if (user.gender && (user.age || user.ageGroup)) {
      navigate("/dashboard");
    }
  }, [user, navigate, isEditMode]);

  useEffect(() => {
    if (!user || !isEditMode) return;
    form.setFieldsValue({
      isDeaf: user.isDeaf ?? false,
      age: user.age,
      gender: user.gender || "prefiro-nao-informar",
      isParentOfDeafPerson: user.isParentOfDeafPerson ?? false,
      isLibrasInterpreter: user.isLibrasInterpreter ?? false,
      isLibrasEnthusiast: user.isLibrasEnthusiast ?? false,
      country: user.country || "Brasil",
      state: user.state,
      city: user.city,
      librasFluencyLevel: user.librasFluencyLevel || "basico",
    });
  }, [user, isEditMode, form]);

  const stepFieldGroups: Record<number, string[]> = {
    0: ["isDeaf", "age", "gender"],
    1: [
      "isParentOfDeafPerson",
      "isLibrasInterpreter",
      "isLibrasEnthusiast",
      "country",
      "state",
      "city",
    ],
    2: ["librasFluencyLevel"],
  };

  const handleNext = async () => {
    try {
      const fields = stepFieldGroups[currentStep];
      const country = form.getFieldValue("country");
      const toValidate =
        currentStep === 1
          ? fields.filter((f) => {
              if (country !== "Brasil" && (f === "state" || f === "city")) {
                return false;
              }
              return true;
            })
          : fields;

      await form.validateFields(toValidate);
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        form.submit();
      }
    } catch {
      /* validação antd */
    }
  };

  const prev = () => setCurrentStep((s) => Math.max(0, s - 1));

  const handleFinish = async (values: Record<string, unknown>) => {
    try {
      const country = values.country as string;
      const payload = {
        isDeaf: !!values.isDeaf,
        age: Number(values.age),
        gender: values.gender as string,
        isParentOfDeafPerson: !!values.isParentOfDeafPerson,
        isLibrasInterpreter: !!values.isLibrasInterpreter,
        isLibrasEnthusiast: !!values.isLibrasEnthusiast,
        country: country || null,
        state: country === "Brasil" ? (values.state as string) || null : null,
        city: country === "Brasil" ? (values.city as string) || null : null,
        librasFluencyLevel: (values.librasFluencyLevel as string) || null,
      };

      await updateUserProfile(payload as Partial<UserProps>, {
        skipSuccessToast: true,
      });
      navigate(isEditMode ? "/profile" : "/dashboard");
    } catch (error) {
      console.error("Failed to complete registration:", error);
    }
  };

  const getSubtitle = () => {
    if (currentStep === 0) return "Quem você é e como quer ser chamado";
    if (currentStep === 1) return "Contexto e onde você mora";
    return "Seu contato com Libras";
  };

  const getSubmitLabel = () => {
    if (currentStep < 2) return "Continuar";
    if (isEditMode) return "Salvar";
    return "Salvar e continuar";
  };

  return (
    <MobileLayout
      headerTitle={isEditMode ? "Editar perfil" : "Completar perfil"}
      headerType="private"
    >
      <MobileFormCard>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Title>{isEditMode ? "Editar perfil" : "Complete seu perfil"}</Title>
          <Subtitle>{getSubtitle()}</Subtitle>
        </div>

        <StepsWrap>
          <Steps
            current={currentStep}
            size="small"
            items={[{ title: "Você" }, { title: "Sobre" }, { title: "Libras" }]}
          />
        </StepsWrap>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          requiredMark={false}
          initialValues={{
            isDeaf: false,
            gender: "prefiro-nao-informar",
            country: "Brasil",
            isParentOfDeafPerson: false,
            isLibrasInterpreter: false,
            isLibrasEnthusiast: false,
            librasFluencyLevel: "basico",
          }}
        >
          {currentStep === 0 && (
            <StepContainer>
              <Form.Item
                name="isDeaf"
                label="Você é surdo ou ouvinte?"
                rules={[{ required: true, message: "Selecione uma opção" }]}
              >
                <RadioGroup>
                  <Radio.Group style={{ width: "100%" }} buttonStyle="solid">
                    <Radio.Button value>Surdo</Radio.Button>
                    <Radio.Button value={false}>Ouvinte</Radio.Button>
                  </Radio.Group>
                </RadioGroup>
              </Form.Item>

              <Form.Item
                name="age"
                label="Qual sua idade?"
                rules={[{ required: true, message: "Informe sua idade" }]}
              >
                <Input
                  type="number"
                  min={1}
                  max={120}
                  placeholder="Ex: 25"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="gender"
                label="Gênero"
                rules={[{ required: true, message: "Selecione seu gênero" }]}
              >
                <Select placeholder="Selecione" size="large">
                  <Select.Option value="masculino">Masculino</Select.Option>
                  <Select.Option value="feminino">Feminino</Select.Option>
                  <Select.Option value="nao-binario">Não-binário</Select.Option>
                  <Select.Option value="transgenero">Transgênero</Select.Option>
                  <Select.Option value="homem-trans">Homem Trans</Select.Option>
                  <Select.Option value="mulher-trans">
                    Mulher Trans
                  </Select.Option>
                  <Select.Option value="genero-fluido">
                    Gênero fluido
                  </Select.Option>
                  <Select.Option value="agenero">Agênero</Select.Option>
                  <Select.Option value="outro">Outro</Select.Option>
                  <Select.Option value="prefiro-nao-informar">
                    Prefiro não informar
                  </Select.Option>
                </Select>
              </Form.Item>
            </StepContainer>
          )}

          {currentStep === 1 && (
            <StepContainer>
              <Form.Item label="Sobre você (opcional)">
                <Form.Item
                  name="isParentOfDeafPerson"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Sou pai/mãe de pessoa surda</Checkbox>
                </Form.Item>
                <div style={{ marginTop: 8 }}>
                  <Form.Item
                    name="isLibrasInterpreter"
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox>Sou intérprete de Libras</Checkbox>
                  </Form.Item>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Form.Item
                    name="isLibrasEnthusiast"
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox>Sou entusiasta/estudante de Libras</Checkbox>
                  </Form.Item>
                </div>
              </Form.Item>

              <Form.Item
                name="country"
                label="País"
                rules={[{ required: true, message: "Selecione seu país" }]}
              >
                <Select
                  showSearch
                  placeholder="Selecione seu país"
                  size="large"
                  filterOption={(input, option) =>
                    String(option?.children || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {COUNTRIES.map((c) => (
                    <Select.Option key={c.code} value={c.name}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.country !== cur.country}
              >
                {({ getFieldValue }) =>
                  getFieldValue("country") === "Brasil" ? (
                    <>
                      <Form.Item
                        name="state"
                        label="Estado"
                        rules={[
                          { required: true, message: "Selecione seu estado" },
                        ]}
                      >
                        <Select placeholder="Estado" size="large">
                          {BRAZIL_STATES.map((s) => (
                            <Select.Option key={s.code} value={s.name}>
                              {s.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name="city"
                        label="Cidade"
                        rules={[
                          { required: true, message: "Informe sua cidade" },
                        ]}
                      >
                        <Input placeholder="Sua cidade" size="large" />
                      </Form.Item>
                    </>
                  ) : null
                }
              </Form.Item>
            </StepContainer>
          )}

          {currentStep === 2 && (
            <StepContainer>
              <Form.Item
                name="librasFluencyLevel"
                label="Nível de fluência em Libras"
              >
                <Select placeholder="Selecione" size="large">
                  {LIBRAS_LEVEL_DB.map((o) => (
                    <Select.Option key={o.value} value={o.value}>
                      {o.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </StepContainer>
          )}

          <StepActions>
            {currentStep > 0 ? (
              <Button onClick={prev} size="large">
                Voltar
              </Button>
            ) : (
              <div style={{ minWidth: 96 }} aria-hidden />
            )}
            <Button
              type="primary"
              size="large"
              loading={loadingAuth}
              onClick={() => {
                if (currentStep < 2) {
                  handleNext().catch(() => undefined);
                } else {
                  form.submit();
                }
              }}
              style={{ minWidth: 160, borderRadius: 10 }}
            >
              {getSubmitLabel()}
            </Button>
          </StepActions>
        </Form>
      </MobileFormCard>
    </MobileLayout>
  );
};

export default CompleteRegistration;
